import React, { useContext, useEffect, useMemo, useState, useRef } from "react";
import { GlobalContext } from "../../Context/GlobalContext";
import { toast } from "sonner";
import {
  ClipboardList,
  Eye,
  X,
  Mail,
  Phone,
  MapPin,
  Building2,
  FileDown,
  RefreshCcw,
} from "lucide-react";
import Pagination from "../Pagination";
import "../Sales Person/CreateSalesPerson.css";
import { exportAnyJsonToExcel } from "../Utils/exportAnyJsonToExcel";
import SalesAnalytics from "../../Components/Sales Person/SalesAnalist";
import LoadingScreen from "../Utils/LoadingScreen";

function SalesVisits() {
  const { api, Products = [], adminId } = useContext(GlobalContext);

  // Fallback adminId
  const sessionAdminId = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("userData");
      if (!raw) return "";
      const parsed = JSON.parse(raw);
      return parsed?.adminMeta?.adminId ? String(parsed.adminMeta.adminId) : "";
    } catch {
      return "";
    }
  }, []);
  const effectiveAdminId = adminId || sessionAdminId;
  const today = new Date().toISOString().split("T")[0];
  const [selectedProduct, setSelectedProduct] = useState("");
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [search, setSearch] = useState("");
  const [detailRow, setDetailRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Add ref to track if initial load is done
  const initialLoadDone = useRef(false);

  // Prefill product if only one exists
  useEffect(() => {
    if (!selectedProduct && Products?.length === 1) {
      setSelectedProduct(Products[0]?.pk_id ?? "");
    }
  }, [Products, selectedProduct]);

  const productOptions = useMemo(
    () =>
      (Products || []).map((p) => ({
        value: p.pk_id ?? p.pkId ?? p.id ?? "",
        label: p.product_name ?? p.name ?? "Unknown",
      })),
    [Products]
  );

  const getAllLatestSalesVisit = async (adminIdParam, productId, from, to) => {
    setLoading(true);
    const startTime = Date.now();
    try {
      const payload = {
        adminId: String(adminIdParam),
        productId: String(productId),
        fromDate: from || "",
        toDate: to || "",
      };
      const res = await api.post(`/Admin/v1.0/getAllLatestSalesVisit`, payload);
      if (res?.data?.responseCode === "00") {
        const sortedData = (res.data.data || []).sort(compareDatesDesc);
        setRows(sortedData);
        // setRows(res.data.data || []);
      } else {
        setRows([]);
        toast.error(
          res?.data?.responseMessage || "Failed to fetch sales analytics"
        );
      }
    } catch (e) {
      setRows([]);
      toast.error("Something went wrong!");
    } finally {
      const elapsed = Date.now() - startTime;
      const minDelay = 500;

      if (elapsed < minDelay) {
        setTimeout(() => setLoading(false), minDelay - elapsed);
      } else {
        setLoading(false);
      }
    }
  };

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (search.trim() !== "") {
        const s = search.toLowerCase();
        const fields = [
          r.business_nature,
          r.contact_person_name,
          r.prospect_business_name,
          r.email_id,
          r.salespersonMobile,
        ];
        if (!fields.some((f) => f?.toLowerCase().includes(s))) {
          return false;
        }
      }
      return true;
    });
  }, [rows, search]);

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return filteredRows.slice(start, end);
  }, [filteredRows, currentPage]);

  const openDetails = (row) => setDetailRow(row);
  const closeDetails = () => setDetailRow(null);

  // Main data fetching
  useEffect(() => {
    if (effectiveAdminId && selectedProduct) {
      getAllLatestSalesVisit(
        effectiveAdminId,
        selectedProduct,
        fromDate,
        toDate
      );
      setCurrentPage(1);
      initialLoadDone.current = true;
    } else {
      setRows([]);
      setLoading(false);
    }
  }, [effectiveAdminId, selectedProduct, fromDate, toDate]);

  if (loading) {
    return (
      <div className="relative min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center">
        <LoadingScreen />
      </div>
    );
  }

  return (
    <div className="aoc-csp-screen w-full min-h-[90svh] bg-gradient-to-b from-gray-50 to-white shadow rounded-2xl">
      <div className="aoc-csp-container w-full px-4 py-6 md:py-4">
        <div className="aoc-csp-header flex items-center justify-between gap-4 mb-6 md:mb-10 animate-fade-in">
          <div className="flex items-center gap-3">
            <div className="aoc-csp-badge flex items-center justify-center h-12 w-12 rounded-2xl bg-blue-100 shadow-sm">
              <ClipboardList className="h-6 w-6 text-blue-500" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-semibold text-gray-800 tracking-tight">
                Sales Visits
              </h1>
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-end gap-4">
            <div className="min-w-[160px]">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email..."
                className="aoc-csp-control-new "
              />
            </div>
            <div className="flex items-center gap-3">
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="custom-date-input bg-white dark:bg-blue-800 dark:text-gray-100"
              />
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="custom-date-input bg-white dark:bg-gray-800 dark:text-gray-100"
              />
            </div>

            <div className="flex items-end gap-3 flex-wrap">
              <button
                className="btn btn-refresh"
                onClick={() =>
                  getAllLatestSalesVisit(
                    effectiveAdminId,
                    selectedProduct,
                    fromDate,
                    toDate
                  )
                }
              >
                <RefreshCcw size={14} />
                <span>Refresh</span>
              </button>

              <button
                className="btn btn-export"
                onClick={() =>
                  exportAnyJsonToExcel({
                    data: filteredRows,
                    filePrefix: "outletExport",
                    sheetName: "outletList",
                  })
                }
              >
                <FileDown size={14} />
                <span>Export</span>
              </button>

              <div className="min-w-[100px]">
                <label className="aoc-csp-label ml-2">Product</label>
                <div className="aoc-csp-control-new">
                  <select
                    className="aoc-csp-input w-[100px] text-sm rounded outline-none"
                    value={selectedProduct}
                    onChange={(e) => setSelectedProduct(e.target.value)}
                  >
                    <option value="">Select product</option>
                    {productOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* top card */}
        {initialLoadDone.current && effectiveAdminId && selectedProduct && (
          <SalesAnalytics
            key={`${effectiveAdminId}-${selectedProduct}-${fromDate}-${toDate}`}
            adminId={effectiveAdminId}
            productId={selectedProduct}
            fromDate={fromDate}
            toDate={toDate}
            autoFetch={true}
          />
        )}

        <div className="aoc-csp-card relative rounded-2xl animate-rise">
          <div className="aoc-csp-table-wrapper overflow-x-auto ">
            <table className="aoc-csp-table w-full">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Farm Name</th>
                  <th>Visitor Info</th>
                  <th>Visit Type</th>
                  <th>Sales Person Info</th>
                  <th>Date/Time</th>
                  <th>Address</th>
                  <th className="w-[120px] text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="py-6 text-center">
                      <LoadingScreen />
                    </td>
                  </tr>
                ) : paginatedRows?.length > 0 ? (
                  paginatedRows.map((r, idx) => (
                    <tr
                      key={idx}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="text-gray-700 font-medium">
                        {(currentPage - 1) * itemsPerPage + idx + 1}
                      </td>
                      <td className="text-gray-700 font-medium">
                        {r.prospect_business_name || "N/A"}
                      </td>
                      <td className="text-gray-700">
                        <div className="flex flex-col space-y-1">
                          <span>{r.contact_person_name || "N/A"} </span>
                          <span>{r.contact_person_mobile || "N/A"} </span>
                        </div>
                      </td>
                      <td className="text-gray-700">
                        <div className="flex flex-col space-y-1">
                          <span>{r.visit_type || "N/A"}</span>
                        </div>
                      </td>
                      <td className="text-gray-700">
                        <div className="flex flex-col space-y-1">
                          <span>{r.salesPersonName || "N/A"}</span>
                          <span>{r.salespersonMobile || "N/A"}</span>
                        </div>
                      </td>
                      <td className="text-gray-900">
                        <div className="flex items-center gap-2">
                          <span>{safeDate(r.dsr_date)}</span>
                        </div>
                      </td>
                      <td className="text-gray-700">
                        <div>{r.address}</div>
                        <div>
                          {capitalize(r.state)}
                          {r.zone_name ? ` / ${capitalize(r.zone_name)}` : ""}
                        </div>
                      </td>

                      <td className="text-center">
                        <div className="flex items-center gap-3 mx-2">
                          <button
                            className="aoc-csp-icon-btn text-gray-600 hover:text-gray-900"
                            title="View"
                            onClick={() => openDetails(r)}
                          >
                            <Eye className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="py-10 text-center text-gray-500">
                      No sales visits found for selected filters
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Details Modal */}
        {detailRow && (
          <div className="aoc-csp-modal" role="dialog" aria-modal="true">
            <div className="aoc-csp-modal-backdrop" onClick={closeDetails} />
            <div className="aoc-csp-modal-card animate-rise">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-blue-100 grid place-items-center border border-gray-200">
                    <ClipboardList className="h-5 w-5 text-blue-500" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">
                      Visit Details
                    </h3>
                    <p className="text-xs text-gray-500">
                      {safeDate(detailRow.dsr_date)}
                    </p>
                  </div>
                </div>
                <button
                  className="aoc-csp-icon-btn"
                  onClick={closeDetails}
                  title="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoRow
                  icon={<Building2 className="h-4 w-4" />}
                  label="Business"
                  value={detailRow.prospect_business_name || "N/A"}
                />
                <InfoRow
                  icon={<MapPin className="h-4 w-4" />}
                  label="State / Zone"
                  value={`${capitalize(detailRow.state) || "N/A"}${
                    detailRow.zone_name
                      ? " / " + capitalize(detailRow.zone_name)
                      : ""
                  }`}
                />
                <InfoRow
                  icon={<Phone className="h-4 w-4" />}
                  label="Contact Mobile"
                  value={detailRow.contact_person_mobile || "N/A"}
                />
                <InfoRow
                  icon={<Mail className="h-4 w-4" />}
                  label="Email"
                  value={detailRow.email_id || "N/A"}
                />
                <InfoRow
                  label="Contact Person"
                  value={detailRow.contact_person_name || "N/A"}
                />
                <InfoRow
                  label="Prospect Status"
                  value={detailRow.prospect_status || "N/A"}
                />
                <InfoRow
                  label="Business Nature"
                  value={detailRow.business_nature || "N/A"}
                />
                <InfoRow
                  label="Present Business"
                  value={detailRow.present_business_nature || "N/A"}
                />
                <InfoRow label="Fssai" value={detailRow.has_fssai || "N/A"} />
                <InfoRow label="Gst" value={detailRow.has_gst || "N/A"} />
                <InfoRow label="Latitude" value={detailRow.latitude || "N/A"} />
                <InfoRow
                  label="Longitude"
                  value={detailRow.longitude || "N/A"}
                />
                <InfoRow label="Dsr Date" value={detailRow.dsr_date || "N/A"} />
                <InfoRow
                  label="Established Date"
                  value={detailRow.established_date || "N/A"}
                />
                <InfoRow
                  label="Sales Person Name"
                  value={detailRow.salesPersonName || "N/A"}
                />
                <InfoRow
                  label="Sales Person Code"
                  value={detailRow.salesPersonCode || "N/A"}
                />
                <InfoRow
                  label="Sales Person Mobile"
                  value={detailRow.salespersonMobile || "N/A"}
                />
                 <InfoRow
                  label="Visit Type"
                  value={detailRow.visit_type || "N/A"}
                />
                <InfoRow
                  label="MBNK Interested"
                  value={detailRow.mbnk_interested || "N/A"}
                />
                <InfoRow
                  label="MBNK Interested Prospect"
                  value={detailRow.mbnk_interested_prospect || "N/A"}
                />
                <InfoRow
                  label="MBNK Demo"
                  value={detailRow.mbnk_demo || "N/A"}
                />
                <InfoRow
                  label="Upfront Payment Due"
                  value={detailRow.upfront_payment_due || "N/A"}
                />
                <InfoRow
                  label="Lead Follow Update"
                  value={detailRow.lead_follow_update || "N/A"}
                />
                <div className="md:col-span-2">
                  <div className="aoc-csp-label mb-1">Address</div>
                  <div className="aoc-csp-control">
                    <textarea
                      className="aoc-csp-input min-h-[80px]"
                      readOnly
                      value={detailRow.address || "N/A"}
                    />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <div className="aoc-csp-label mb-1">Remarks</div>
                  <div className="aoc-csp-control">
                    <textarea
                      className="aoc-csp-input min-h-[80px]"
                      readOnly
                      value={detailRow.remarks || "N/A"}
                    />
                  </div>
                </div>
              </div>

              <div className="mt-5">
                <div className="aoc-csp-label mb-2">Images</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <ImageTile
                    label="Prospect Outlet Pic"
                    url={detailRow.prospect_outlet_pic}
                  />
                  <ImageTile label="Shop Image" url={detailRow.shop_image} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {filteredRows.length > itemsPerPage && (
        <div className="mt-5 pb-2 flex justify-center">
          <Pagination
            totalItems={filteredRows.length}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>
      )}
    </div>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div>
      <div className="aoc-csp-label mb-1">{label}</div>
      <div className="aoc-csp-control">
        {icon ? <span className="aoc-csp-icon">{icon}</span> : null}
        <input className="aoc-csp-input" value={value || "N/A"} readOnly />
      </div>
    </div>
  );
}

function ImageTile({ label, url }) {
  const isValid =
    typeof url === "string" &&
    url.trim() !== "" &&
    url.trim().toLowerCase() !== "string" &&
    url.startsWith("http");

  const handleClick = (e) => {
    e.stopPropagation();
    if (isValid) {
      const newTab = window.open();
      newTab.document.write(`
        <html>
          <head>
            <title>${label}</title>
            <style>
              body {
                margin: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                background: #000;
                height: 100vh;
              }
              img {
                width: 800px;  
                height: 500px;  
                object-fit: contain;
              }
            </style>
          </head>
          <body>
            <img src="${url}" alt="${label}" />
          </body>
        </html>
      `);
      newTab.document.close();
    }
  };

  return (
    <div className="aoc-csp-tile-container">
      <div
        className={`aoc-csp-doc-tile ${
          isValid ? "cursor-pointer" : "cursor-default"
        }`}
        onClick={isValid ? handleClick : undefined}
      >
        <div className="aoc-csp-doc-thumb">
          {isValid ? (
            <img
              src={url}
              alt={label}
              className="aoc-csp-thumb-img"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.replaceWith(
                  Object.assign(document.createElement("div"), {
                    className: "aoc-csp-doc-empty",
                    textContent: "Image not available",
                  })
                );
              }}
            />
          ) : (
            <div className="aoc-csp-doc-empty">Image not available</div>
          )}
        </div>
      </div>

      <div className="aoc-csp-doc-caption">{label}</div>
    </div>
  );
}

/* -------- helpers -------- */
function safeDate(d) {
  if (!d) return "-";
  const parsed = new Date(d);
  if (isNaN(parsed.getTime())) return d;
  return parsed.toLocaleString();
}
function capitalize(s) {
  if (!s || typeof s !== "string") return s || "";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const compareDatesDesc = (a, b) => {
  const dateA = new Date(a.dsr_date).getTime();
  const dateB = new Date(b.dsr_date).getTime();
  return dateB - dateA;
};

export default SalesVisits;
